import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button as MuiButton,
  IconButton,
} from "@mui/material";
import { Close as XIcon, Error as AlertCircleIcon } from "@mui/icons-material";
import {
  RequireIcon as CustomRequireIcon,
  documentIcon,
} from "@ippon/shared-assets";
import { useSharedColors } from "../../../index";

export interface CustomPdfUploaderProps {
  name: string;
  label: string;
  register: any;
  setValue: any;
  errors?: any;
  width?: string;
  type?: "single" | "multiple";
  accept?: string;
  maxSize?: number; // MB
  required?: boolean;
  description?: string;
  initialFiles?: File[];
}

const CustomPdfUploader: React.FC<CustomPdfUploaderProps> = ({
  name,
  label,
  register,
  setValue,
  errors = {},
  width = "338px",
  type = "multiple",
  accept = ".pdf",
  maxSize = 20,
  required = false,
  description,
  initialFiles = [],
}) => {
  const colors = useSharedColors();
  const [files, setFiles] = useState<File[]>(initialFiles);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // register for required rule only; value is managed via setValue
  React.useEffect(() => {
    if (register) {
      register(name, {
        ...(required && {
          required: `${label}は必須です` as any,
          validate: (value: any) => {
            if (Array.isArray(value) && value.length > 0) return true;
            if (!Array.isArray(value) && value) return true;
            return `${label}は必須です`;
          },
        }),
      });
    }
  }, [register, name, required, label]);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSize * 1024 * 1024) {
        return `${file.name}は${maxSize}MBを超えています`;
      }
      const acceptedTypes = accept
        .split(",")
        .map((t) => t.trim().toLowerCase());
      const fileExtension =
        "." + (file.name.split(".").pop() || "").toLowerCase();
      if (!acceptedTypes.includes(fileExtension)) {
        return `${file.name}は対応していないファイル形式です`;
      }
      return null;
    },
    [accept, maxSize]
  );

  const handleFiles = useCallback(
    (newFiles: FileList | null) => {
      if (!newFiles) return;
      setUploadError(null);
      const fileArray = Array.from(newFiles);

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          setUploadError(error);
          return;
        }
      }

      let updated: File[];
      if (type === "single") {
        updated = [fileArray[0]];
      } else {
        updated = [...files, ...fileArray];
      }

      setFiles(updated);
      // shouldDirty: 親フォームの dirty 検知に乗せる (FixedActionBar の活性化に必要)
      setValue(name, updated, { shouldValidate: true, shouldDirty: true });
    },
    [files, type, validateFile, name, setValue]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  const removeFile = useCallback(
    (index: number) => {
      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
      setValue(name, updatedFiles.length > 0 ? updatedFiles : null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setUploadError(null);
      if (inputRef.current) inputRef.current.value = "";
    },
    [files, name, setValue]
  );

  // Note: size display removed to match Figma spec (secondary text is just "PDF")

  const hasError = errors && (errors as any)[name];

  return (
    <Box sx={{ width: "100%", maxWidth: width }}>
      <Typography
        sx={{
          fontSize: "12px !important",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          color: colors.text.primary,
          pb: "1px",
          mb: "6px",
          fontWeight: 700,
        }}
      >
        {label}
        {required && (
          <span style={{ display: "flex", marginLeft: "4px" }}>
            <CustomRequireIcon />
          </span>
        )}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, mb: 1.5, fontSize: "12px" }}
        >
          {description}
        </Typography>
      )}

      <MuiButton
        variant="outlined"
        onClick={() => inputRef.current?.click()}
        sx={{
          width: "100%",
          height: 34,
          fontSize: "12px",
          borderWidth: 1,
          borderColor: "#D9D9D9",
          color: "#0B9DBD",

          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          "&:hover": {
            borderColor: "#D9D9D9",
            bgcolor: "#FFFFFF",
          },
          "&:active, &:focus, &:focus-visible": {
            borderColor: "#D9D9D9",
            borderWidth: 2,
            outline: "none",
            boxShadow: "none",
            bgcolor: "#FFFFFF",
          },
        }}
      >
        書類アップロード
      </MuiButton>

      <input
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        accept={accept}
        multiple={type === "multiple"}
        onChange={handleFileInputChange}
      />

      {hasError && (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "error.main",
          }}
        >
          <AlertCircleIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="error">
            {(hasError?.message as string) || "アップロードエラー"}
          </Typography>
        </Box>
      )}

      {uploadError && (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "error.main",
          }}
        >
          <AlertCircleIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="error">
            {uploadError}
          </Typography>
        </Box>
      )}

      {files.length > 0 && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {files.map((file, index) => (
            <Box
              key={`${file.name}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                pl: "10px",
                pr: "12px",
                py: "8px",
                bgcolor: "#FFFFFF",
                borderRadius: "10px",
                border: "1px solid #D9D9D9",
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={documentIcon}
                  alt="document"
                  style={{ width: 24, height: 24 }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    lineHeight: 1.7,
                    color: "#3E3E3E",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>
                {/* Secondary line removed per request to reduce height */}
              </Box>
              <IconButton
                onClick={() => removeFile(index)}
                size="small"
                sx={{
                  flexShrink: 0,
                  ml: "auto",
                  width: 24,
                  height: 24,
                  color: "#3E3E3E",
                  "&:hover": {
                    bgcolor: "transparent",
                  },
                }}
              >
                <XIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomPdfUploader;
